// src/components/ReviewModal.jsx
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiX, FiSend, FiStar, FiCheckCircle } from "react-icons/fi";
import app from "../firebase";
import { getFirestore, collection, addDoc, serverTimestamp } from "firebase/firestore";
import { useToast } from "../context/ToastContext";

// ---- rules-aligned constraints (mirror your firestore.rules) ----
const LIMITS = { nameMax: 100, messageMin: 5, messageMax: 2000, locationMax: 100, emailMax: 200 };
const EMAIL_RE = /^([\w-.+])+@([\w-]+\.)+[A-Za-z]{2,}$/;

// Toast compat — supports either ctx.toast({ ... }) or ctx.addToast(message[, opts])
function useToastCompat() {
  const ctx = (() => { try { return useToast?.(); } catch { return null; } })();
  return ({ type = "info", message = "", title = "" }) => {
    if (!ctx) return;
    if (typeof ctx.toast === "function") {
      ctx.toast({
        title: title || (type === "error" ? "Something went wrong" : "Notice"),
        description: message,
        variant: type === "error" ? "destructive" : "default",
      });
      return;
    }
    if (typeof ctx.addToast === "function") {
      try {
        if (ctx.addToast.length >= 2) ctx.addToast(String(message), { type, title });
        else ctx.addToast(String(message));
      } catch {
        ctx.addToast(String(message));
      }
    }
  };
}

export default function ReviewModal({
  open,
  onClose,
  brokerName = "",
  brokerId = "",
  className = "",
}) {
  const db = getFirestore(app);
  const toast = useToastCompat();
  const overlayRef = React.useRef(null);

  // form state
  const [loading, setLoading] = React.useState(false);
  const [submittedOK, setSubmittedOK] = React.useState(false); // show Thank You
  const [rating, setRating] = React.useState(5);
  const [form, setForm] = React.useState({
    name: "",
    email: "",
    location: "",
    message: "",
    consent: false,
    company: "", // honeypot
  });

  // validation state
  const [errors, setErrors] = React.useState({});
  const [touched, setTouched] = React.useState({}); // {name:true,...}
  const [attempted, setAttempted] = React.useState(false); // user tried to submit

  // close on ESC
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && open && !loading) onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, loading, onClose]);

  const onOverlayClick = (e) => { if (e.target === overlayRef.current && !loading) onClose?.(); };

  const setVal = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const markTouched = (e) => {
    const { name } = e.target;
    if (!name) return;
    setTouched((t) => ({ ...t, [name]: true }));
  };

  const computeErrors = React.useCallback(() => {
    const next = {};
    const name = form.name.trim();
    const email = form.email.trim();
    const location = form.location.trim();
    const message = form.message.trim();

    if (!name) next.name = "Full name is required.";
    else if (name.length > LIMITS.nameMax) next.name = `Name must be ≤ ${LIMITS.nameMax} characters.`;

    if (email) {
      if (email.length > LIMITS.emailMax) next.email = `Email must be ≤ ${LIMITS.emailMax} characters.`;
      else if (!EMAIL_RE.test(email)) next.email = "Please enter a valid email.";
    }

    if (location && location.length > LIMITS.locationMax)
      next.location = `City, State must be ≤ ${LIMITS.locationMax} characters.`;

    if (!message) next.message = "Your review is required.";
    else if (message.length < LIMITS.messageMin)
      next.message = `Review must be at least ${LIMITS.messageMin} characters.`;
    else if (message.length > LIMITS.messageMax)
      next.message = `Review must be ≤ ${LIMITS.messageMax} characters.`;

    if (!form.consent) next.consent = "Please agree to moderation terms.";
    if (!(rating >= 1 && rating <= 5)) next.rating = "Please select a rating 1–5.";
    return next;
  }, [form.name, form.email, form.location, form.message, form.consent, rating]);

  // keep errors fresh as the user types
  React.useEffect(() => {
    setErrors(computeErrors());
  }, [computeErrors]);

  const showErr = (key) => (attempted || touched[key]) && errors[key];

  const charCountColor =
    (attempted || touched.message) && form.message.trim().length < LIMITS.messageMin
      ? "text-red-600"
      : "text-gray-500";

  function humanizeFirestoreError(err) {
    const code = err?.code || "";
    if (code.includes("permission-denied"))
      return "Your review didn’t pass validation. Please check the highlighted fields.";
    if (code.includes("unavailable"))
      return "Network hiccup. Please try again.";
    return "Something went wrong. Please try again.";
  }

  async function submit(e) {
    e.preventDefault();
    if (form.company) return; // honeypot

    setAttempted(true);
    const next = computeErrors();
    setErrors(next);
    if (Object.keys(next).length) {
      toast({ type: "error", message: "Please fix the highlighted fields." });
      return;
    }

    try {
      setLoading(true);
      // Firestore-safe payload: only allowed, non-empty optional fields
      const payload = {
        name: form.name.trim(),
        rating: Math.max(1, Math.min(5, Number.isFinite(Number(rating)) ? Number(rating) : 5)),
        message: form.message.trim(),
        status: "pending",
        createdAt: serverTimestamp(),
      };
      if (form.email.trim()) payload.email = form.email.trim();
      if (form.location.trim()) payload.location = form.location.trim();
      if (brokerName.trim()) payload.brokerName = brokerName.trim();
      if (brokerId) payload.brokerId = String(brokerId);

      await addDoc(collection(db, "testimonials_submissions"), payload);

      setSubmittedOK(true);
      toast({ type: "info", message: "Thanks! Your review was sent for approval." });
      setLoading(false);
      // Keep values for the Thank You preview; we'll clear if they opt to add another
    } catch (err) {
      console.error(err);
      toast({ type: "error", message: humanizeFirestoreError(err) });
      setLoading(false);
    }
  }

  const resetForm = () => {
    setForm({ name: "", email: "", location: "", message: "", consent: false, company: "" });
    setRating(5);
    setErrors({});
    setTouched({});
    setAttempted(false);
  };

  const handleClose = () => {
    resetForm();
    setSubmittedOK(false);
    onClose?.();
  };

  const firstName = form.name.trim().split(" ")[0] || "There";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={overlayRef}
          onMouseDown={onOverlayClick}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={`fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm flex items-center justify-center px-4 ${className}`}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="review-modal-title"
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 24, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="relative w-full max-w-xl rounded-2xl bg-white shadow-xl"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => !loading && handleClose()}
              className="absolute right-3 top-3 p-2 text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              <FiX size={20} />
            </button>

            {/* HEADER */}
            <div className="p-6 border-b border-gray-100">
              <div className="mb-1 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase text-[#002B5C]">
                <span className="inline-flex items-center bg-[#e8f1fb] px-2.5 py-1 rounded-full">
                  Share your experience
                </span>
                {brokerName && (
                  <span className="text-gray-400">•</span>
                )}
                {brokerName && (
                  <span className="inline-flex items-center bg-gray-100 px-2.5 py-1 rounded-full text-gray-600">
                    {brokerName}
                  </span>
                )}
              </div>
              <h3 id="review-modal-title" className="text-xl md:text-2xl font-extrabold text-[#002B5C]">
                {submittedOK ? "Thank you for your review" : "Leave a Review"}
              </h3>
            </div>

            {/* BODY */}
            <div className="p-6">
              {!submittedOK ? (
                <form onSubmit={submit} className="space-y-4" noValidate>
                  {/* Rating */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Your Rating</label>
                    <div className="mt-1 flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          type="button"
                          key={n}
                          onClick={() => setRating(n)}
                          className={`p-1 ${n <= rating ? "text-yellow-400" : "text-gray-300"}`}
                          aria-label={`${n} star`}
                        >
                          <FiStar className={n <= rating ? "fill-yellow-400" : ""} size={22} />
                        </button>
                      ))}
                      <span className="ml-2 text-sm text-gray-600">
                        {rating}/5
                      </span>
                    </div>
                    {showErr("rating") && <p className="mt-1 text-sm text-red-600">{errors.rating}</p>}
                  </div>

                  {/* Name & Email */}
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium">Full Name *</label>
                      <input
                        name="name"
                        value={form.name}
                        onChange={setVal}
                        onBlur={markTouched}
                        required
                        aria-invalid={!!showErr("name")}
                        className={`mt-1 w-full border rounded-xl p-3 focus:outline-none focus:ring-2 ${
                          showErr("name") ? "border-red-500 focus:ring-red-500" : "focus:ring-blue-600"
                        }`}
                        placeholder="Jane Doe"
                      />
                      {showErr("name") && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium">Email (private)</label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={setVal}
                        onBlur={markTouched}
                        aria-invalid={!!showErr("email")}
                        className={`mt-1 w-full border rounded-xl p-3 focus:outline-none focus:ring-2 ${
                          showErr("email") ? "border-red-500 focus:ring-red-500" : "focus:ring-blue-600"
                        }`}
                        placeholder="name@example.com"
                      />
                      {showErr("email") && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                    </div>
                  </div>

                  {/* Location */}
                  <div>
                    <label className="block text-sm font-medium">City, State</label>
                    <input
                      name="location"
                      value={form.location}
                      onChange={setVal}
                      onBlur={markTouched}
                      aria-invalid={!!showErr("location")}
                      className={`mt-1 w-full border rounded-xl p-3 focus:outline-none focus:ring-2 ${
                        showErr("location") ? "border-red-500 focus:ring-red-500" : "focus:ring-blue-600"
                      }`}
                      placeholder="Austin, TX"
                    />
                    {showErr("location") && <p className="mt-1 text-sm text-red-600">{errors.location}</p>}
                  </div>

                  {/* Review text */}
                  <div>
                    <label className="block text-sm font-medium">Your Review *</label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={setVal}
                      onBlur={markTouched}
                      required
                      rows={5}
                      aria-invalid={!!showErr("message")}
                      className={`mt-1 w-full border rounded-xl p-3 focus:outline-none focus:ring-2 ${
                        showErr("message") ? "border-red-500 focus:ring-red-500" : "focus:ring-blue-600"
                      }`}
                      placeholder="What went well? What stood out?"
                    />
                    <div className="mt-1 flex justify-between text-xs">
                      <span className="text-gray-500">Minimum {LIMITS.messageMin} characters</span>
                      <span className={charCountColor}>
                        {form.message.length}/{LIMITS.messageMax}
                      </span>
                    </div>
                    {showErr("message") && <p className="mt-1 text-sm text-red-600">{errors.message}</p>}
                  </div>

                  {/* Consent & honeypot */}
                  <input
                    type="text"
                    name="company"
                    value={form.company}
                    onChange={setVal}
                    className="hidden"
                    tabIndex={-1}
                    autoComplete="off"
                  />
                  <label className="flex items-start gap-3 text-sm">
                    <input
                      type="checkbox"
                      name="consent"
                      checked={form.consent}
                      onChange={setVal}
                      onBlur={markTouched}
                      className="mt-1"
                    />
                    <span>
                      I agree that my review may be published after moderation. See our{" "}
                      <a href="/terms" className="underline">Terms</a>.
                    </span>
                  </label>
                  {showErr("consent") && <p className="mt-1 text-sm text-red-600">{errors.consent}</p>}

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => !loading && handleClose()}
                      className="px-4 py-2 rounded-xl border"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-[#0B4AA2] text-white font-semibold hover:opacity-95 disabled:opacity-60"
                      disabled={loading}
                    >
                      {loading ? (
                        <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                        </svg>
                      ) : (
                        <FiSend />
                      )}
                      {loading ? "Submitting..." : "Submit Review"}
                    </button>
                  </div>
                </form>
              ) : (
                // ---- THANK YOU UI ----
                <div className="text-center">
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.25 }}
                    className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white shadow-lg"
                  >
                    <FiCheckCircle size={28} />
                  </motion.div>

                  <h4 className="mt-4 text-2xl font-extrabold text-[#002B5C]">
                    Thanks, {firstName}!
                  </h4>
                  <p className="mt-2 text-gray-600">
                    We’ve received your review{brokerName ? <> for <span className="font-semibold">{brokerName}</span></> : ""}.
                    Our team moderates submissions for accuracy and respectful language before publishing.
                  </p>

                  {/* Preview card */}
                  <div className="mt-6 text-left mx-auto max-w-md">
                    <div className="rounded-2xl border border-gray-100 shadow-sm p-5 bg-white">
                      <div className="flex items-center gap-2 text-yellow-500 mb-2">
                        {Array.from({ length: Math.max(1, Math.min(5, rating || 5)) }).map((_, i) => (
                          <FiStar key={i} className="fill-yellow-400" />
                        ))}
                        <span className="ml-1 text-sm text-gray-600">{rating}/5</span>
                      </div>
                      <p className="text-gray-800 whitespace-pre-line">{form.message || "No message provided."}</p>
                      <div className="mt-4 pt-4 border-t border-gray-100 text-sm text-gray-600 flex flex-wrap gap-x-3 gap-y-1">
                        <span className="font-semibold text-[#002B5C]">{form.name}</span>
                        {form.location && <span>• {form.location}</span>}
                        {form.email && <span>• {form.email}</span>}
                        {brokerName && <span>• {brokerName}</span>}
                      </div>
                    </div>
                  </div>

                  <p className="mt-4 text-xs text-gray-500">
                    Typical moderation time is within 1–2 business days. We’ll only contact you if clarification is needed.
                  </p>

                  <div className="mt-6 flex items-center justify-center gap-3">
                    <button
                      onClick={handleClose}
                      className="px-4 py-2 rounded-xl border"
                    >
                      Close
                    </button>
                    <button
                      onClick={() => { setSubmittedOK(false); resetForm(); }}
                      className="px-5 py-2.5 rounded-2xl bg-[#0B4AA2] text-white font-semibold hover:opacity-95"
                    >
                      Leave another review
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
